import React, { useState, useEffect } from 'react';
import { Shield, CheckCircle, FileText, BarChart3, GitBranch, Scale, Award, MessageSquare, ClipboardCheck, Download, X, ArrowRight, ChevronRight } from 'lucide-react';

const STORAGE_KEY = 'cyberboard_welcomed_v1';

const features = [
  {
    icon: CheckCircle,
    color: 'text-green-500',
    bg: 'bg-green-50',
    tab: 'assessment',
    title: 'Maturity Assessment',
    description: 'Score your board\'s cybersecurity oversight across 5 domains and get a gap analysis with a maturity level from Initial to Optimizing.',
  },
  {
    icon: FileText,
    color: 'text-blue-500',
    bg: 'bg-blue-50',
    tab: 'reporting',
    title: 'Board Reporting Frameworks',
    description: 'Learn how to frame cybersecurity risk in business language your board actually understands — plus reporting cadence and dashboard structure.',
  },
  {
    icon: BarChart3,
    color: 'text-purple-500',
    bg: 'bg-purple-50',
    tab: 'metrics',
    title: 'Metrics & KPIs Catalog',
    description: 'A curated set of board-appropriate KPIs across risk posture, resilience, compliance, and financial exposure — with target ranges.',
  },
  {
    icon: Scale,
    color: 'text-orange-500',
    bg: 'bg-orange-50',
    tab: 'compliance',
    title: 'SEC Compliance Guide',
    description: 'Navigate the SEC\'s 2023 cybersecurity disclosure rules: 4-day Form 8-K incident deadlines, 10-K annual requirements, and board expertise.',
  },
  {
    icon: Award,
    color: 'text-indigo-500',
    bg: 'bg-indigo-50',
    tab: 'frameworks',
    title: 'Global Frameworks',
    description: 'Map NIST CSF 2.0, ISO 27001:2022, DORA, NIS2, and CIS Controls to your board\'s governance requirements with a comparison matrix.',
  },
  {
    icon: ClipboardCheck,
    color: 'text-teal-500',
    bg: 'bg-teal-50',
    tab: 'prep',
    title: 'Meeting Prep Wizard',
    description: 'Step-by-step guide for preparing your next board or audit committee presentation — agenda, key messages, Q&A anticipation, and more.',
  },
  {
    icon: Download,
    color: 'text-rose-500',
    bg: 'bg-rose-50',
    tab: 'templates',
    title: 'Ready-to-Use Templates',
    description: 'Copy-paste board reporting decks, incident notification drafts, committee charter language, and director briefing one-pagers.',
  },
];

export default function WelcomeModal({ onNavigate }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (!localStorage.getItem(STORAGE_KEY)) {
      setOpen(true);
    }
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setOpen(false);
  };

  const goToTab = (tab) => {
    dismiss();
    if (onNavigate) onNavigate(tab);
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" role="dialog" aria-modal="true" aria-labelledby="welcome-title">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={dismiss} aria-hidden="true" />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 px-6 pt-6 pb-5 text-white flex-shrink-0">
          <button
            onClick={dismiss}
            className="absolute top-4 right-4 text-white/70 hover:text-white transition-colors"
            aria-label="Close welcome guide"
          >
            <X size={20} />
          </button>
          <div className="flex items-center gap-3 mb-2">
            <div className="bg-white/20 rounded-xl p-2">
              <Shield size={22} className="text-white" />
            </div>
            <span className="text-sm font-medium text-blue-200 uppercase tracking-wide">CyberBoard by CISO Marketplace</span>
          </div>
          <h2 id="welcome-title" className="text-2xl font-bold mb-1">Welcome to CyberBoard</h2>
          <p className="text-blue-100 text-sm leading-relaxed">
            Your interactive toolkit for CISO-Board alignment, governance reporting, and strategic cybersecurity oversight — free for CISO Marketplace members (or credits / one-time guest pass); sign in to save your report. See microsec.tools/pricing.
          </p>
        </div>

        {/* Step indicator */}
        {step === 0 && (
          <div className="px-6 py-3 bg-blue-50 border-b border-blue-100 flex-shrink-0">
            <p className="text-xs text-blue-600 font-medium">
              How to use &rarr; Click any section below to jump straight to it, or close this and explore the tabs across the top.
            </p>
          </div>
        )}

        {/* Feature grid — scrollable */}
        <div className="overflow-y-auto flex-1 px-6 py-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {features.map((f) => {
              const Icon = f.icon;
              return (
                <button
                  key={f.tab}
                  onClick={() => goToTab(f.tab)}
                  className={`text-left rounded-xl p-4 border border-gray-100 hover:border-blue-200 hover:shadow-md transition-all group ${f.bg}`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`mt-0.5 flex-shrink-0 ${f.color}`}>
                      <Icon size={18} />
                    </div>
                    <div className="min-w-0">
                      <div className="flex items-center gap-1">
                        <span className="text-sm font-semibold text-gray-800 group-hover:text-blue-700 transition-colors">{f.title}</span>
                        <ChevronRight size={13} className="text-gray-400 group-hover:text-blue-500 transition-colors flex-shrink-0" />
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{f.description}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between flex-shrink-0 bg-gray-50">
          <p className="text-xs text-gray-400">
            This guide won&apos;t show again — find it anytime via the <strong>Overview</strong> tab.
          </p>
          <button
            onClick={() => goToTab('overview')}
            className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors"
          >
            Start Exploring <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
